import { useState, useEffect, useSyncExternalStore } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, ChefHat, Leaf, Heart, X, Plus, Loader2, Info, Timer, Check } from "lucide-react";
import RecipeCard from "@/components/studio/RecipeCard";
import RecipeModal from "@/components/studio/RecipeModal";
import AddToPlanDialog from "@/components/studio/AddToPlanDialog";
import { DietaryChips } from "@/components/studio/DietaryChips";

const MODES = [
  { id: "pantry", label: "Pantry-to-Plate", icon: Sparkles, hint: "Cook with what you have" },
  { id: "health", label: "Health Coach", icon: Heart, hint: "Hit your macro goals" },
  { id: "zerowaste", label: "Zero-Waste", icon: Leaf, hint: "Use up soon-to-expire items" },
];

const MEAL_TYPES = ["Breakfast", "Lunch", "Dinner", "Snack"];

const DISCLAIMER = "General wellness guidance, not medical advice. Consult your clinician.";

const GEN_STORE_KEY = "nourishcraft:gen:v1";
const DEFAULT_DRAFT = { mode:"pantry", ingredients:[], input:"", dietary:[], mealType:"Dinner", goal:"", useItUp:false, dietTemplate:null, selectedIds:[], selectedPantryIds:[], excludeExpired:true, pantryItems:[], allergies:"", avoid:"" };
const generationStore = {
  loading: false,
  recipes: [],
  draft: Object.assign({}, DEFAULT_DRAFT),
  ownerId: null,
  listeners: new Set(),
  _snap: null,
  _updateSnap() { this._snap = { loading: this.loading, recipes: this.recipes, draft: this.draft }; },
  set(patch) {
    if (patch && typeof patch === "object") {
      if ("loading" in patch) this.loading = patch.loading;
      if ("recipes" in patch) this.recipes = patch.recipes;
      if ("draft" in patch) this.draft = patch.draft;
      this._updateSnap();
      try { const { allergies: _a, avoid: _av, dietary: _d, dietTemplate: _dt, goal: _g, pantryItems: _pi, ...safeDraft } = this.draft; localStorage.setItem(GEN_STORE_KEY, JSON.stringify({ recipes: this.recipes, draft: safeDraft, ownerId: this.ownerId })); } catch (e) {}
      this.listeners.forEach((l) => { try { l(); } catch (e) {} });
    }
  }
};
generationStore._updateSnap();
try {
  const raw = localStorage.getItem(GEN_STORE_KEY);
  if (raw) {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      if (Array.isArray(parsed.recipes)) generationStore.recipes = parsed.recipes;
      if (typeof parsed.ownerId === "string") generationStore.ownerId = parsed.ownerId;
      if (parsed.draft && typeof parsed.draft === "object") generationStore.draft = Object.assign({}, DEFAULT_DRAFT, parsed.draft);
      generationStore._updateSnap();
    }
  }
} catch (e) {}
function subscribeGeneration(listener) {
  generationStore.listeners.add(listener);
  return function () { generationStore.listeners.delete(listener); };
}

// Per-account cache hygiene: the persisted generation payload is stamped with
// the owning user's id. On login, a mismatched (or legacy unstamped) payload
// is purged before any view renders it. Logout always purges.
export function clearGenerationStore() {
  generationStore.loading = false;
  generationStore.recipes = [];
  generationStore.draft = Object.assign({}, DEFAULT_DRAFT);
  generationStore.ownerId = null;
  generationStore._updateSnap();
  try { localStorage.removeItem(GEN_STORE_KEY); } catch (e) {}
  generationStore.listeners.forEach((l) => { try { l(); } catch (e) {} });
}

export function reconcileGenerationStoreOwner(userId) {
  let storedOwner = null;
  let hasStored = false;
  try {
    const raw = localStorage.getItem(GEN_STORE_KEY);
    if (raw) {
      hasStored = true;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.ownerId === "string") storedOwner = parsed.ownerId;
    }
  } catch (e) {}
  if (hasStored && storedOwner && storedOwner === userId) {
    generationStore.ownerId = userId;
    return "kept";
  }
  clearGenerationStore();
  generationStore.ownerId = userId;
  return hasStored ? "purged" : "kept";
}
function getGenerationSnapshot() { return generationStore._snap; }

// Staged status messages shown on the Generate button while recipes are being created.
const GEN_STAGES = [
  "Reading your ingredients\u2026",
  "Crafting recipes\u2026",
  "Adding photos & nutrition\u2026",
];
// Client-side timeout for recipe generation (~12 minutes).
const GEN_TIMEOUT_MS = 12 * 60 * 1000;

export default function GenerateView({ onUpgrade, sub, onOpenCoach }) {
  const genSnap = useSyncExternalStore(subscribeGeneration, getGenerationSnapshot);
  const mode = genSnap.draft.mode;
  const setMode = (v) => generationStore.set({ draft: { ...generationStore.draft, mode: (typeof v === "function" ? v(generationStore.draft.mode) : v) } });
  const ingredients = genSnap.draft.ingredients;
  const setIngredients = (v) => generationStore.set({ draft: { ...generationStore.draft, ingredients: (typeof v === "function" ? v(generationStore.draft.ingredients) : v) } });
  const input = genSnap.draft.input;
  const setInput = (v) => generationStore.set({ draft: { ...generationStore.draft, input: (typeof v === "function" ? v(generationStore.draft.input) : v) } });
  const dietary = genSnap.draft.dietary;
  const setDietary = (v) => generationStore.set({ draft: { ...generationStore.draft, dietary: (typeof v === "function" ? v(generationStore.draft.dietary) : v) } });
  const mealType = genSnap.draft.mealType;
  const setMealType = (v) => generationStore.set({ draft: { ...generationStore.draft, mealType: (typeof v === "function" ? v(generationStore.draft.mealType) : v) } });
  const goal = genSnap.draft.goal;
  const setGoal = (v) => generationStore.set({ draft: { ...generationStore.draft, goal: (typeof v === "function" ? v(generationStore.draft.goal) : v) } });

  const loading = genSnap.loading;
  const setLoading = (v) => generationStore.set({ loading: typeof v === "function" ? v(genSnap.loading) : v });
  const recipes = genSnap.recipes;
  const setRecipes = (v) => generationStore.set({ recipes: typeof v === "function" ? v(genSnap.recipes) : v });
  const [viewRecipe, setViewRecipe] = useState(null);
  const [planRecipe, setPlanRecipe] = useState(null);
    const [savedIds, setSavedIds] = useState([]);
  const [genStage, setGenStage] = useState(0);
  const [genError, setGenError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const dietTemplate = genSnap.draft.dietTemplate;
  const setDietTemplate = (v) => generationStore.set({ draft: { ...generationStore.draft, dietTemplate: (typeof v === "function" ? v(generationStore.draft.dietTemplate) : v) } });
  const useItUp = genSnap.draft.useItUp;
  const setUseItUp = (v) => generationStore.set({ draft: { ...generationStore.draft, useItUp: (typeof v === "function" ? v(generationStore.draft.useItUp) : v) } });
  const [members, setMembers] = useState([]);
  const selectedIds = genSnap.draft.selectedIds;
  const setSelectedIds = (v) => generationStore.set({ draft: { ...generationStore.draft, selectedIds: (typeof v === "function" ? v(generationStore.draft.selectedIds) : v) } }); const selectedPantryIds = genSnap.draft.selectedPantryIds || []; const setSelectedPantryIds = (v) => generationStore.set({ draft: { ...generationStore.draft, selectedPantryIds: (typeof v === "function" ? v(generationStore.draft.selectedPantryIds || []) : v) } }); const excludeExpired = genSnap.draft.excludeExpired !== false; const setExcludeExpired = (v) => generationStore.set({ draft: { ...generationStore.draft, excludeExpired: v } });
  const allergies = genSnap.draft.allergies;
  const setAllergies = (v) => generationStore.set({ draft: { ...generationStore.draft, allergies: (typeof v === "function" ? v(generationStore.draft.allergies) : v) } });
  const avoid = genSnap.draft.avoid;
  const setAvoid = (v) => generationStore.set({ draft: { ...generationStore.draft, avoid: (typeof v === "function" ? v(generationStore.draft.avoid) : v) } });

  useEffect(() => {
    (async () => {
      try {
        const [t, p, h] = await Promise.all([
          api.get("/diet-templates"), api.get("/profile"), api.get("/household"),
        ]);
        setTemplates(t.data?.templates || []);
        const prof = p.data || {};
        setDietTemplate(prof.diet_template || null);
        setUseItUp(!!prof.use_it_up);
        const mem = h.data?.members || [];
        setMembers(mem);
        const owner = mem.find((m) => m.is_owner);
        setSelectedIds(owner ? [owner.member_id] : mem.slice(0, 1).map((m) => m.member_id));
      } catch {
        /* non-blocking: generation still works with defaults */
      }
    })();
  }, []);

  const persistProfile = (patch) => {
    api.put("/profile", { diet_template: dietTemplate, use_it_up: useItUp, ...patch }).catch(() => {});
  };

  const activeTemplate = templates.find((t) => t.id === dietTemplate) || null;

  const toggleMember = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const selectedMembers = members.filter((m) => selectedIds.includes(m.member_id));

  // Combined constraint summary for the selected crew (+ account-level inputs).
  const constraintSummary = (() => {
    const diets = new Set(dietary);
    const allergyParts = [];
    const avoidParts = [];
    if (allergies.trim()) allergyParts.push(allergies.trim());
    if (avoid.trim()) avoidParts.push(avoid.trim());
    selectedMembers.forEach((m) => {
      (m.dietary || []).forEach((d) => diets.add(d));
      if ((m.allergies || "").trim()) allergyParts.push(m.allergies.trim());
      if ((m.avoid || "").trim()) avoidParts.push(m.avoid.trim());
    });
    const names = selectedMembers.map((m) => (m.is_owner ? "Just me" : m.name)).join(" + ") || "nobody selected";
    const bits = [`Cooking for ${names}`];
    if (diets.size) bits.push([...diets].join(", "));
    if (allergyParts.length) bits.push(`allergies: ${[...new Set(allergyParts)].join(", ")}`);
    if (avoidParts.length) bits.push(`avoids: ${[...new Set(avoidParts)].join(", ")}`);
    return bits.join(" · ");
  })();

  useEffect(() => { let cancelled = false; (async () => { try { const d0 = generationStore.draft; const res = await api.get("/pantry"); if (!cancelled) generationStore.set({ draft: { ...generationStore.draft, pantryItems: res.data || [] } }); } catch {} })(); return () => { cancelled = true; }; }, []); const togglePantryChip = (item) => { const id = item.id; const isExp = item.expires_at ? new Date(item.expires_at) < new Date(new Date().toDateString()) : false; if (isExp && excludeExpired) { toast.error("Expired items are excluded"); return; } const cur = selectedPantryIds || []; const isSel = cur.includes(id); setSelectedPantryIds(isSel ? cur.filter((x) => x !== id) : [...cur, id]); const nm = (item.name || "").trim(); if (!nm) return; if (isSel) setIngredients(ingredients.filter((g) => g.toLowerCase() !== nm.toLowerCase())); else if (!ingredients.some((g) => g.toLowerCase() === nm.toLowerCase())) setIngredients([...ingredients, nm]); }; const addIngredient = (e) => {
    e.preventDefault();
    const v = input.trim();
    if (v && !ingredients.includes(v)) setIngredients([...ingredients, v]);
    setInput("");
  };

  const toggleDietary = (d) =>
    setDietary((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  const generate = async () => {
    if (mode !== "health" && ingredients.length === 0) {
      toast.error("Add a few ingredients first");
      return;
    }
    setLoading(true);
    setRecipes([]);
    setGenError(null);
    setGenStage(0);
    // Rotate staged status messages so the button never looks stuck.
    const stageTimer = setInterval(() => {
      setGenStage((s) => (s + 1) % GEN_STAGES.length);
    }, 5000);
    // Client-side timeout: abort the request if it runs too long.
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, GEN_TIMEOUT_MS);
    try {
      const res = await api.post("/recipes/generate", {
        ingredients, dietary, mode, goal, meal_type: mealType, count: 3,
        diet_template: dietTemplate, use_it_up: useItUp,
        member_ids: selectedIds, allergies, avoid,
        household_size: Math.max(1, selectedIds.length),
      }, { signal: controller.signal, timeout: GEN_TIMEOUT_MS });
      setRecipes(res.data.recipes);
      toast.success(`Crafted ${res.data.recipes.length} recipes`);
    } catch (e) {
      const status = e?.response?.status;
      const serverDetail = e?.response?.data?.detail;
      const serverMsg = typeof serverDetail === "string" && serverDetail ? serverDetail : null;
      if (status === 402) {
        toast.error("Your free month has ended");
        onUpgrade && onUpgrade();
      } else if (timedOut || e?.code === "ECONNABORTED" || e?.name === "CanceledError") {
        const msg = "This is taking longer than expected \u2014 please try again.";
        setGenError(msg);
        toast.error(msg);
      } else {
        // Always surface the API's error message when it provides one -- never silent.
        const msg = serverMsg || "Recipe generation failed. Try again.";
        setGenError(msg);
        toast.error(msg);
      }
    } finally {
      clearTimeout(timeoutId);
      clearInterval(stageTimer);
      setLoading(false);
    }
  };

  const saveRecipe = async (recipe) => {
    try {
      await api.post("/recipes/save", { recipe });
      setSavedIds((p) => [...p, recipe.id]);
      toast.success("Saved to your vault");
    } catch (e) {
      toast.error("Could not save");
    }
  };

  return (
    <div>
      <header className="mb-8">
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#C85A32] font-medium">AI Culinary Studio</span>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">Craft your next meal</h1>
        <p className="text-[#6E6259] mt-2">Tell us what you have and how you eat — we'll do the cooking brainstorm.</p>
      </header>
      {onOpenCoach && (
        <button onClick={onOpenCoach} className="w-full flex items-center gap-3 p-4 mb-6 rounded-2xl border border-[#E2D9CC] bg-white text-left hover:shadow-md transition-shadow">
          <div className="w-10 h-10 rounded-xl bg-[#F4EFE6] flex items-center justify-center">
            <ChefHat className="w-5 h-5 text-[#C85A32]" />
          </div>
          <div>
            <div className="font-medium text-sm">Ask your chef</div>
            <div className="text-xs text-[#9C8F85]">Your personal chef's assistant — knows your pantry, your restrictions, your week.</div>
          </div>
        </button>
      )}

      {sub && sub.status === "trialing" && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 rounded-2xl bg-[#FDF5E6] border border-[#E9D9B8] px-5 py-3.5" data-testid="trial-banner">
          <span className="text-sm text-[#8a6d2a]">
            🎉 You're on your <b>free month</b> — {sub.days_left ?? 0} days of unlimited recipes left.
          </span>
          <button onClick={onUpgrade} className="text-sm font-medium text-[#C85A32] hover:underline">Lock in Premium →</button>
        </div>
      )}
      {sub && sub.status === "expired" && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 rounded-2xl bg-[#2C221E] px-5 py-4" data-testid="paywall-banner">
          <span className="text-sm text-white">Your free month has ended. Subscribe to keep generating recipes & dish photos.</span>
          <button onClick={onUpgrade} data-testid="banner-upgrade-btn" className="text-sm font-medium bg-[#C85A32] hover:bg-[#B04B26] text-white rounded-full px-4 py-2 transition-colors">Go Premium</button>
        </div>
      )}

      {/* Mode selector */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {MODES.map((m) => {
          const active = mode === m.id;
          return (
            <button
              key={m.id}
              data-testid={`mode-${m.id}`}
              onClick={() => setMode(m.id)}
              className={`flex items-center gap-3 p-4 rounded-2xl border text-left transition-all duration-200 ${active ? "border-[#C85A32] bg-white shadow-md" : "border-[#E2D9CC] bg-white/50 hover:bg-white"}`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${active ? "bg-[#C85A32] text-white" : "bg-[#F4EFE6] text-[#6E6259]"}`}>
                <m.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="font-medium text-sm">{m.label}</div>
                <div className="text-xs text-[#9C8F85]">{m.hint}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Builder card */}
      <div className="bg-white rounded-2xl border border-[#E2D9CC] p-6 md:p-7">
        <label className="text-xs font-mono uppercase tracking-wider text-[#9C8F85]">
          {mode === "zerowaste" ? "Ingredients to use up" : "Available ingredients"}
        </label>
        {(genSnap.draft.pantryItems || []).length > 0 && (<div className="mt-3"><div className="flex items-center justify-between mb-2"><span className="text-xs font-mono uppercase tracking-wider text-[#9C8F85]">From your pantry{(selectedPantryIds || []).length > 0 && <span className="ml-2 px-2 py-0.5 rounded-full bg-[#C85A32] text-white text-[11px]">{(selectedPantryIds || []).length} selected</span>}</span><label className="flex items-center gap-1.5 text-xs text-[#9C8F85] cursor-pointer select-none"><input type="checkbox" checked={excludeExpired} onChange={(e) => setExcludeExpired(e.target.checked)} className="w-4 h-4 accent-[#C85A32]" />Exclude expired</label></div><div className="flex flex-wrap gap-2">{[...(genSnap.draft.pantryItems || [])].sort((a, b) => { const da = a.expires_at ? new Date(a.expires_at).getTime() : Infinity; const db = b.expires_at ? new Date(b.expires_at).getTime() : Infinity; return da - db; }).map((it) => { const isExp = it.expires_at ? new Date(it.expires_at) < new Date(new Date().toDateString()) : false; const isSel = (selectedPantryIds || []).includes(it.id); const dis = isExp && excludeExpired; return <button key={it.id} type="button" disabled={dis} onClick={() => togglePantryChip(it)} className={`min-h-[44px] px-3 py-2 rounded-full border text-sm transition-colors ${isSel ? "bg-[#C85A32] text-white border-[#C85A32]" : "bg-white text-[#3B563E] border-[#E2D9CC]"} ${isExp ? "opacity-50" : ""}`}>{it.name}</button>; })}</div></div>)}<form onSubmit={addIngredient} className="flex gap-2 mt-2">
          <Input
            data-testid="ingredient-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="e.g. chicken thighs, spinach, lemon…"
            className="rounded-xl border-[#E2D9CC] bg-[#FBF9F5] h-11 focus-visible:ring-[#C85A32]"
          />
          <Button type="submit" data-testid="add-ingredient-btn" className="rounded-xl bg-[#2C221E] hover:bg-[#3d2f28] h-11 px-4">
            <Plus className="w-5 h-5" />
          </Button>
        </form>
        {ingredients.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {ingredients.map((ing) => (
              <span key={ing} className="flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-full bg-[#E8F0E6] text-sm text-[#3B563E]">
                {ing}
                <button onClick={() => setIngredients(ingredients.filter((x) => x !== ing))} className="hover:text-[#C85A32]">
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="mt-6">
          <label className="text-xs font-mono uppercase tracking-wider text-[#9C8F85]">Dietary restrictions</label>
          <div className="mt-2">
            <DietaryChips selected={dietary} onToggle={toggleDietary} />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 mt-6">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#9C8F85]">Allergies (hard vetoes)</label>
            <Input
              data-testid="generator-allergies-input"
              value={allergies}
              onChange={(e) => setAllergies(e.target.value)}
              placeholder="e.g. shellfish, sesame"
              className="rounded-xl border-[#E2D9CC] bg-[#FBF9F5] h-10 mt-2 focus-visible:ring-[#C85A32]"
            />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#9C8F85]">Ingredients to avoid</label>
            <Input
              data-testid="generator-avoid-input"
              value={avoid}
              onChange={(e) => setAvoid(e.target.value)}
              placeholder="e.g. cilantro, mushrooms"
              className="rounded-xl border-[#E2D9CC] bg-[#FBF9F5] h-10 mt-2 focus-visible:ring-[#C85A32]"
            />
          </div>
        </div>

        {/* Diet plan templates */}
        <div className="mt-6">
          <label className="text-xs font-mono uppercase tracking-wider text-[#9C8F85]">Diet plan template</label>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
            <button
              data-testid="template-none"
              onClick={() => { setDietTemplate(null); persistProfile({ diet_template: null }); }}
              className={`px-3 py-2.5 rounded-xl text-sm border text-left transition-colors ${!dietTemplate ? "bg-[#2C221E] text-white border-[#2C221E]" : "bg-[#FBF9F5] text-[#6E6259] border-[#E2D9CC] hover:border-[#C5B8A5]"}`}
            >
              <div className="font-medium">No template</div>
              <div className={`text-xs ${!dietTemplate ? "text-white/70" : "text-[#9C8F85]"}`}>Free-form recipes</div>
            </button>
            {templates.map((t) => {
              const active = dietTemplate === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`template-${t.id}`}
                  onClick={() => { setDietTemplate(t.id); persistProfile({ diet_template: t.id }); }}
                  className={`px-3 py-2.5 rounded-xl text-sm border text-left transition-colors ${active ? "bg-[#4A6B4E] text-white border-[#4A6B4E]" : "bg-[#FBF9F5] text-[#6E6259] border-[#E2D9CC] hover:border-[#C5B8A5]"}`}
                >
                  <div className="font-medium leading-tight">{t.label}</div>
                  <div className={`text-xs ${active ? "text-white/70" : "text-[#9C8F85]"}`}>{t.tagline}</div>
                </button>
              );
            })}
          </div>
          {activeTemplate && (
            <div data-testid="template-disclaimer" className="mt-3 flex items-start gap-2 rounded-xl bg-[#FDF5E6] border border-[#E9D9B8] px-3 py-2.5 text-xs text-[#8a6d2a]">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                <b>{activeTemplate.label}</b> · target ~{activeTemplate.macros?.calories} kcal, {activeTemplate.macros?.protein}g protein/day.
                {" "}{DISCLAIMER}
              </span>
            </div>
          )}
        </div>

        {/* Cooking for (household members + guests) + Use-it-up */}
        <div className="grid sm:grid-cols-2 gap-5 mt-6">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#9C8F85]">Cooking for</label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="cooking-for">
              {members.map((m) => {
                const checked = selectedIds.includes(m.member_id);
                const label = m.is_owner ? "Just me" : m.name;
                return (
                  <button
                    key={m.member_id}
                    data-testid={`cook-member-${m.member_id}`}
                    onClick={() => toggleMember(m.member_id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm border transition-colors ${checked ? "bg-[#C85A32] text-white border-[#C85A32]" : "bg-[#FBF9F5] text-[#6E6259] border-[#E2D9CC] hover:border-[#C5B8A5]"}`}
                  >
                    <span className={`w-4 h-4 rounded flex items-center justify-center border ${checked ? "bg-white border-white" : "border-[#C5B8A5]"}`}>
                      {checked && <Check className="w-3 h-3 text-[#C85A32]" />}
                    </span>
                    {label}
                    {m.is_guest && <span className="text-[9px] font-mono uppercase bg-white/25 rounded-full px-1.5 py-0.5">guest</span>}
                  </button>
                );
              })}
            </div>
            <p data-testid="constraint-summary" className="text-[11px] text-[#6E6259] mt-2 leading-relaxed">{constraintSummary}</p>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#9C8F85]">Pantry priority</label>
            <button
              data-testid="use-it-up-toggle"
              onClick={() => { const v = !useItUp; setUseItUp(v); persistProfile({ use_it_up: v }); }}
              className={`w-full mt-2 flex items-center justify-between gap-2 py-2.5 px-3.5 rounded-xl text-sm border transition-colors ${useItUp ? "bg-[#4A6B4E] text-white border-[#4A6B4E]" : "bg-[#FBF9F5] text-[#6E6259] border-[#E2D9CC] hover:border-[#C5B8A5]"}`}
            >
              <span className="flex items-center gap-2"><Timer className="w-4 h-4" /> Use it up first</span>
              <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${useItUp ? "bg-white/40" : "bg-[#D8CDBD]"}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${useItUp ? "translate-x-4" : "translate-x-0.5"}`} />
              </span>
            </button>
            <p className="text-[11px] text-[#9C8F85] mt-1.5">Prioritizes recipes using items expiring soonest.</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-6">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#9C8F85]">Meal type</label>
            <div className="flex gap-2 mt-2">
              {MEAL_TYPES.map((m) => (
                <button
                  key={m}
                  onClick={() => setMealType(m)}
                  className={`flex-1 py-2 rounded-xl text-sm border transition-colors ${mealType === m ? "bg-[#4A6B4E] text-white border-[#4A6B4E]" : "bg-[#FBF9F5] text-[#6E6259] border-[#E2D9CC]"}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          {mode === "health" && (
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#9C8F85]">Health goal</label>
              <Input
                data-testid="goal-input"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder="e.g. high protein, 500 kcal, low sugar"
                className="rounded-xl border-[#E2D9CC] bg-[#FBF9F5] h-10 mt-2 focus-visible:ring-[#C85A32]"
              />
            </div>
          )}
        </div>

        {genError && !loading && (
          <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-800">{genError}</p>
            <Button
              onClick={generate}
              className="shrink-0 rounded-xl bg-[#C85A32] hover:bg-[#B04B26] text-white text-sm px-4 py-2"
            >
              Try again
            </Button>
          </div>
        )}
        <Button
          data-testid="generate-btn"
          onClick={generate}
          disabled={loading}
          className="w-full mt-7 h-13 py-6 rounded-xl bg-[#C85A32] hover:bg-[#B04B26] text-white text-base"
        >
          {loading ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {GEN_STAGES[genStage]}</>) : (<><Sparkles className="w-5 h-5 mr-2" /> Generate recipes</>)}
        </Button>
      </div>

      {/* Results */}
      {loading && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-2xl border border-[#E2D9CC] overflow-hidden bg-white">
              <div className="h-48 shimmer" />
              <div className="p-5 space-y-3">
                <div className="h-5 w-3/4 shimmer rounded" />
                <div className="h-4 w-full shimmer rounded" />
                <div className="h-16 w-full shimmer rounded" />
              </div>
            </div>
          ))}
        </div>
      )}

      {recipes.length > 0 && (
        <div className="mt-10">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="font-serif text-2xl">Fresh from the studio</h2>
            {activeTemplate && (
              <span data-testid="results-disclaimer" className="flex items-center gap-1.5 text-xs text-[#8a6d2a] bg-[#FDF5E6] border border-[#E9D9B8] rounded-full px-3 py-1.5">
                <Info className="w-3.5 h-3.5" /> {DISCLAIMER}
              </span>
            )}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recipes.map((r) => (
              <RecipeCard
                key={r.id}
                recipe={r}
                onView={setViewRecipe}
                onSave={saveRecipe}
                onAddPlan={setPlanRecipe}
                isSaved={savedIds.includes(r.id)}
              />
            ))}
          </div>
        </div>
      )}

      <RecipeModal recipe={viewRecipe} open={!!viewRecipe} onClose={() => setViewRecipe(null)} />
      <AddToPlanDialog recipe={planRecipe} open={!!planRecipe} onClose={() => setPlanRecipe(null)} />
    </div>
  );
}
